import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Edit2, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { NetworkStatusIndicator } from '@/components/flexe/NetworkStatusIndicator';
import FlexESummaryCard from '@/components/flexe/FlexESummaryCard';
import FlexEResultsCharts from '@/components/flexe/FlexEResultsCharts';
import FlexEResultsTable from '@/components/flexe/FlexEResultsTable';
import FlexEGuideModal from '@/components/flexe/FlexEGuideModal';
import FlexEExportToolbar from '@/components/flexe/FlexEExportToolbar';
import { useFlexESimulation } from '@/hooks/useFlexESimulation';
import { useFlexESimulations } from '@/hooks/useFlexESimulations';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';

export default function CRMFlexEDetail() {
  const navigate = useNavigate();
  const { simId } = useParams<{ simId: string }>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { simulation, isLoading } = useFlexESimulation(simId || null);
  const { deleteSimulation, renameSimulation, isDeleting, isRenaming } = useFlexESimulations();

  const handleRename = () => {
    if (!simulation) return;
    const newName = prompt('Nieuwe naam voor simulatie:', simulation.name);
    if (newName && newName.trim()) {
      renameSimulation({ simId: simulation.id, newName: newName.trim() });
    }
  };

  const handleDelete = () => {
    if (!simId) return;
    deleteSimulation(simId);
    navigate('/crm/flexe');
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Simulatie laden...</p>
        </div>
      </div>
    );
  }

  if (!simulation) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Simulatie niet gevonden</p>
          <Button onClick={() => navigate('/crm/flexe')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar overzicht
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-x-hidden">
      {/* Header - responsive */}
      <div className="flex flex-col gap-4">
        {/* Title Row */}
        <div className="flex items-start gap-3 sm:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/crm/flexe')}
            className="shrink-0 mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold truncate">{simulation.name}</h1>
            {/* Breadcrumb - hidden on very small screens */}
            <Breadcrumb className="hidden sm:block mt-2">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink onClick={() => navigate('/crm/flexe')} className="cursor-pointer">
                    Flex-E Simulaties
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="truncate max-w-[150px] sm:max-w-none">{simulation.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        {/* Action Buttons - wrap on mobile */}
        <div className="flex flex-wrap gap-2">
          <FlexEGuideModal />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRename}
            disabled={isRenaming}
            className="sm:size-default"
          >
            <Edit2 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Hernoemen</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isDeleting}
            className="sm:size-default"
          >
            <Trash2 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Verwijderen</span>
          </Button>
        </div>
      </div>

      <NetworkStatusIndicator />

      {/* Baseline Missing Warning */}
      {simulation.scenario_applied && simulation.baseline_success_rate == null && (
        <Alert variant="default" className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
          <AlertTitle className="text-amber-900 dark:text-amber-100">Geen baseline data beschikbaar</AlertTitle>
          <AlertDescription className="text-amber-800 dark:text-amber-200 space-y-2">
            <p>
              Deze simulatie werd uitgevoerd vóór de baseline vergelijkingsfunctie werd toegevoegd. 
              Hierdoor ontbreekt de vergelijking tussen de huidige situatie (zonder nieuwe machines) en het scenario (met nieuwe machines).
            </p>
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/crm/flexe/new')}
                className="border-amber-600 text-amber-900 hover:bg-amber-100 dark:border-amber-500 dark:text-amber-100 dark:hover:bg-amber-900/30"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Simulatie opnieuw uitvoeren met baseline
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Export Toolbar */}
      <FlexEExportToolbar 
        simulationName={simulation.name || 'simulatie'}
        simulationData={{
          contractLimit: simulation.cap_limited_kw,
          battKw: simulation.batt_kw,
          battKwh: simulation.batt_kwh,
          windowStart: simulation.window_start,
          windowEnd: simulation.window_end,
          successRate: simulation.success_rate || undefined,
          baselineSuccessRate: simulation.baseline_success_rate || undefined,
        }}
      />

      {/* Results */}
      <div className="space-y-6">
        <FlexESummaryCard simId={simulation.id} />
        <FlexEResultsCharts simId={simulation.id} />
        <FlexEResultsTable simId={simulation.id} />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Simulatie verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{simulation.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
